import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useActiveWeb3React } from '../../hooks'
import { useAddPopup, useBlockNumber } from '../application/hooks'
import { AppDispatch, AppState } from '../index'
import { checkedTransaction, finalizeTransaction } from './actions'

export function shouldCheck(
  lastBlockNumber: number,
  tx: { addedTime: number; receipt?: {}; lastCheckedBlockNumber?: number }
): boolean {
  if (tx.receipt) return false
  if (!tx.lastCheckedBlockNumber) return true
  const blocksSinceCheck = lastBlockNumber - tx.lastCheckedBlockNumber
  if (blocksSinceCheck < 1) return false
  const minutesPending = (new Date().getTime() - tx.addedTime) / 1000 / 60
  if (minutesPending > 60) {
    // every 10 blocks if pending for longer than an hour
    return blocksSinceCheck > 9
  } else if (minutesPending > 5) {
    // every 3 blocks if pending more than 5 minutes
    return blocksSinceCheck > 2
  } else {
    // otherwise every block
    return true
  }
}

export default function Updater(): null {
  const { chainId, library } = useActiveWeb3React()
  const lastBlockNumber = useBlockNumber()
  const dispatch = useDispatch<AppDispatch>()
  const state = useSelector<AppState, AppState['transactions']>(state => state.transactions)
  const transactions = chainId ? state[chainId] ?? {} : {}
  const addPopup = useAddPopup()

  // 跟踪当前网络ID，检测网络变化
  const currentChainIdRef = useRef<number | undefined>(chainId)
  const isNetworkChangingRef = useRef(false)

  // 检测网络变化
  useEffect(() => {
    if (currentChainIdRef.current !== undefined && currentChainIdRef.current !== chainId) {
      isNetworkChangingRef.current = true

      // 3秒后认为网络切换完成
      setTimeout(() => {
        isNetworkChangingRef.current = false
      }, 3000)
    }
    currentChainIdRef.current = chainId
  }, [chainId])

  useEffect(() => {
    if (!chainId || !library || !lastBlockNumber) return

    // 如果网络正在变化，跳过交易检查
    if (isNetworkChangingRef.current) {
      return
    }

    Object.keys(transactions)
      .filter(hash => shouldCheck(lastBlockNumber, transactions[hash]))
      .forEach(hash => {
        library
          .getTransactionReceipt(hash)
          .then(receipt => {
            // 再次检查网络是否已经变化
            if (currentChainIdRef.current !== chainId) {
              return
            }

            if (receipt) {
              dispatch(
                finalizeTransaction({
                  chainId,
                  hash,
                  receipt: {
                    blockHash: receipt.blockHash,
                    blockNumber: receipt.blockNumber,
                    contractAddress: receipt.contractAddress,
                    from: receipt.from,
                    status: receipt.status,
                    to: receipt.to,
                    transactionHash: receipt.transactionHash,
                    transactionIndex: receipt.transactionIndex
                  }
                })
              )

              addPopup(
                {
                  txn: {
                    hash,
                    success: receipt.status === 1,
                    summary: transactions[hash]?.summary
                  }
                },
                hash
              )
            } else {
              dispatch(checkedTransaction({ chainId, hash, blockNumber: lastBlockNumber }))
            }
          })
          .catch(error => {
            // 检查是否是网络变化错误
            if (
              (error.message &&
                (error.message.includes('underlying network changed') ||
                  error.message.includes('NETWORK_ERROR') ||
                  error.message.includes('network changed'))) ||
              error.code === 'NETWORK_ERROR' ||
              error.code === -32603
            ) {
              console.debug('Network change error during transaction check, ignoring:', error)
              return
            }

            console.error(`failed to check transaction hash: ${hash}`, error)
          })
      })
  }, [chainId, library, transactions, lastBlockNumber, dispatch, addPopup])

  return null
}
